import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import API_URL from '../config/api';
import AuthShell, { inputClass, primaryButtonClass, ErrorBox } from '../components/AuthShell';
import { SUPPORTED_LANGUAGES } from '../i18n';

interface FormState {
  store_name: string;
  first_name: string;
  last_name: string;
  username: string;
  email: string;
  contact_phone: string;
  city: string;
  preferred_language: string;
  password: string;
}

const Field: React.FC<{
  id: keyof FormState;
  label: string;
  hint?: string;
  type?: string;
  required?: boolean;
  autoComplete?: string;
  value: string;
  onChange: (v: string) => void;
  fieldError?: string;
}> = ({ id, label, hint, type = 'text', required, autoComplete, value, onChange, fieldError }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      id={id}
      name={id}
      type={type}
      required={required}
      autoComplete={autoComplete}
      className={`${inputClass} ${fieldError ? 'border-red-400' : ''}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
    {fieldError ? (
      <p className="mt-1 text-xs text-red-600">{fieldError}</p>
    ) : hint ? (
      <p className="mt-1 text-xs text-gray-500">{hint}</p>
    ) : null}
  </div>
);

const Signup: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState<FormState>({
    store_name: '',
    first_name: '',
    last_name: '',
    username: '',
    email: '',
    contact_phone: '',
    city: '',
    preferred_language: (i18n.language || 'en').slice(0, 2),
    password: '',
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k: keyof FormState) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/register/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (response.status === 400 && typeof data === 'object') {
          const fe: Record<string, string> = {};
          Object.entries(data).forEach(([k, v]) => {
            fe[k] = Array.isArray(v) ? String(v[0]) : String(v);
          });
          setFieldErrors(fe);
          if (fe.non_field_errors || fe.detail) setError(fe.non_field_errors || fe.detail);
        } else {
          setError(data.detail || data.error || t('common.genericError'));
        }
        return;
      }
      setDone(true);
    } catch {
      setError(t('auth.cannotConnect'));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <AuthShell title={t('signup.successTitle')}>
        <div className="bg-white shadow rounded-lg p-6 text-center space-y-4">
          <p className="text-sm text-gray-700">{t('signup.successBody')}</p>
          <Link to="/login" className={primaryButtonClass}>
            {t('signup.goToLogin')}
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={t('signup.title')} subtitle={t('signup.subtitle')} wide>
      <form className="bg-white shadow rounded-lg p-4 sm:p-6 space-y-4" onSubmit={handleSubmit}>
        <ErrorBox message={error} />

        <Field id="store_name" label={t('signup.storeName')} hint={t('signup.storeNameHint')} required value={form.store_name} onChange={set('store_name')} fieldError={fieldErrors.store_name} autoComplete="organization" />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field id="first_name" label={t('signup.firstName')} value={form.first_name} onChange={set('first_name')} fieldError={fieldErrors.first_name} autoComplete="given-name" />
          <Field id="last_name" label={t('signup.lastName')} value={form.last_name} onChange={set('last_name')} fieldError={fieldErrors.last_name} autoComplete="family-name" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field id="email" label={t('signup.email')} type="email" required value={form.email} onChange={set('email')} fieldError={fieldErrors.email} autoComplete="email" />
          <Field id="contact_phone" label={t('signup.phone')} type="tel" required value={form.contact_phone} onChange={set('contact_phone')} fieldError={fieldErrors.contact_phone} autoComplete="tel" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field id="city" label={t('signup.city')} hint={t('signup.cityHint')} required value={form.city} onChange={set('city')} fieldError={fieldErrors.city} autoComplete="address-level2" />
          <div>
            <label htmlFor="preferred_language" className="block text-sm font-medium text-gray-700 mb-1">
              {t('signup.language')}
            </label>
            <select
              id="preferred_language"
              value={form.preferred_language}
              onChange={(e) => set('preferred_language')(e.target.value)}
              className={`${inputClass} min-h-[44px]`}
            >
              {SUPPORTED_LANGUAGES.map((l) => (
                <option key={l} value={l}>{t(`language.${l}`)}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field id="username" label={t('signup.username')} required value={form.username} onChange={set('username')} fieldError={fieldErrors.username} autoComplete="username" />
          <Field id="password" label={t('auth.password')} type="password" hint={t('signup.passwordHint')} required value={form.password} onChange={set('password')} fieldError={fieldErrors.password} autoComplete="new-password" />
        </div>

        <button type="submit" disabled={loading} className={primaryButtonClass}>
          {loading ? t('signup.submitting') : t('signup.submit')}
        </button>

        <p className="text-center text-sm text-gray-600">
          {t('auth.haveAccount')}{' '}
          <Link to="/login" className="font-medium text-blue-600 active:text-blue-700">{t('auth.signIn')}</Link>
        </p>
      </form>
    </AuthShell>
  );
};

export default Signup;
